/* eslint react/jsx-key: 0 */
/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { type WhiteboardRecordInfo } from '../shared-types';
import { RecordIcons } from './RecordIcons';
import { FaIcon } from '../canvasx/FaIcon';
import { Image } from '@lib/canvasx/components/Image';
import { Text } from '@lib/canvasx/components/Text';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { AhaIcon, type AhaIconProps } from '../canvasx/AhaIcon';
import moment from 'moment';
import { pluralize } from '@aha-app/aha-shared';
import { Emoji } from '../canvasx/Emoji';

export const RECORD_TITLE_FONT_SIZE = 14;
export const RECORD_FONT_SIZE = 11;
export const RECORD_ICON_SIZE = 10;
export const RECORD_LINE_HEIGHT = 1.6;

export default class RecordDecorator {
  constructor(
    public record: WhiteboardRecordInfo,
    public recordTypename: WhiteboardRecordInfo['typename']
  ) {}

  static defaultIconForType(
    type: WhiteboardRecordInfo['typename']
  ): CanvaSXNode {
    switch (type) {
      case 'Undefined':
      case 'Task':
        return null;

      case 'NotFound':
        return <FaIcon icon='question' size={RECORD_ICON_SIZE} />;

      case 'Page':
        return <FaIcon icon='file-lines' size={RECORD_ICON_SIZE} />;

      default: {
        const Icon = RecordIcons[type];
        if (!Icon) {
          return null;
        }
        return <Icon size={RECORD_ICON_SIZE} />;
      }
    }
  }

  get icon(): CanvaSXNode {
    const { record } = this;

    switch (record.typename) {
      case 'Undefined':
      case 'NotFound':
        return RecordDecorator.defaultIconForType(this.recordTypename);

      case 'Page':
        if (record.emojiValue) {
          return <Emoji shortCode={record.emojiValue} />;
        }

        return (
          <FaIcon
            // @ts-expect-error The icon prop is strongly typed, but the defaultEmojiIcon is just a string
            icon={record.defaultEmojiIcon.slice(3)}
            size={RECORD_ICON_SIZE}
          />
        );

      case 'IdeaTheme': {
        const color = `${record.colorObject?.hex()}ee`;
        return <RecordIcons.IdeaTheme size={RECORD_ICON_SIZE} color={color} />;
      }

      default:
        return RecordDecorator.defaultIconForType(record.typename);
    }
  }

  get title(): string {
    const { record } = this;
    switch (record.typename) {
      case 'Undefined':
        return 'Loading…';

      case 'NotFound':
        return 'Record not found';

      case 'Page':
        return record.nameOrDefault;

      default:
        return record.name ?? '';
    }
  }

  get subtitle(): string {
    const { record } = this;
    switch (record.typename) {
      case 'Task':
        return record.record?.name ?? '';

      case 'Undefined':
      case 'NotFound':
      case 'Page':
      case 'PdlcStep':
      case 'ReleasePhase':
      case 'IdeaTheme':
      case 'Project':
      case 'Annotation':
        return '';

      default:
        return record.workflowStatus?.name ?? '';
    }
  }

  get identifier() {
    const { record } = this;
    switch (record.typename) {
      case 'Undefined':
      case 'NotFound':
      case 'ReleasePhase':
      case 'Annotation':
        return '';

      case 'Task':
        return 'To-do';

      case 'PdlcStep':
        return 'Step';

      case 'Project':
        return record.referencePrefix;

      default:
        return record.referenceNum;
    }
  }

  get meta(): CanvaSXNode {
    return [
      this.assignee,
      this.score,
      this.dueDate,
      this.votes,
      this.documentDate,
      this.ideas,
    ].filter(Boolean) as CanvaSXNode;
  }

  get assignee(): CanvaSXNode {
    const { record } = this;

    if (!('assignedToUser' in record) || !record.assignedToUser) {
      return null;
    }

    return record.assignedToUser.avatarUrl ? (
      <Image
        key='assignee'
        crossOrigin
        src={record.assignedToUser.avatarUrl}
        width={RECORD_FONT_SIZE}
        height={RECORD_FONT_SIZE}
        rounded
        tooltip={record.assignedToUser.name}
      />
    ) : (
      <Text
        key='assignee'
        lineHeight={RECORD_LINE_HEIGHT}
        size={9}
        tooltip={record.assignedToUser.name}
      >
        {record.assignedToUser.initials}
      </Text>
    );
  }

  get score(): CanvaSXNode {
    const { record } = this;
    if (!('score' in record) || typeof record.score !== 'number') {
      return null;
    }

    return (
      <FlexRow gap={2} verticalAlignment='center'>
        <AhaIcon size={RECORD_FONT_SIZE} icon='product-value-score' />
        <Text lineHeight={RECORD_LINE_HEIGHT} size={RECORD_FONT_SIZE}>
          {record.score}
        </Text>
      </FlexRow>
    );
  }

  get dueDate(): CanvaSXNode {
    const { record } = this;
    if (record.typename !== 'Task') {
      return null;
    }

    let date: string | undefined;
    let icon: AhaIconProps['icon'] | undefined;
    if (record.completedDate) {
      date = record.completedDate as string | undefined;
      icon = 'complete';
    } else if (record.dueDate) {
      date = record.dueDate as string | undefined;
      icon = 'pending';
    }

    if (date && icon) {
      return (
        <FlexRow gap={2} verticalAlignment='center'>
          <AhaIcon size={RECORD_FONT_SIZE} icon={icon} />
          <Text lineHeight={RECORD_LINE_HEIGHT} size={RECORD_FONT_SIZE}>
            {moment(date).format('MMM D, YYYY')}
          </Text>
        </FlexRow>
      );
    }
  }

  get votes(): CanvaSXNode {
    if (
      this.record.typename !== 'Idea' ||
      typeof this.record.votes !== 'number'
    ) {
      return null;
    }

    return (
      <FlexRow
        key='votes'
        gap={2}
        verticalAlignment='center'
        tooltip={`${this.record.votes} ${pluralize(this.record.votes, 'vote')}`}
      >
        <FaIcon size={RECORD_FONT_SIZE} icon='badge-check' style='solid' />
        <Text lineHeight={RECORD_LINE_HEIGHT} size={RECORD_FONT_SIZE}>
          {this.record.votes}
        </Text>
      </FlexRow>
    );
  }

  get ideas(): CanvaSXNode {
    if (
      this.record.typename !== 'IdeaTheme' ||
      typeof this.record.ideasCount !== 'number'
    ) {
      return null;
    }

    return (
      <FlexRow
        key='ideas'
        gap={2}
        verticalAlignment='center'
        tooltip={`${this.record.ideasCount} ${pluralize(
          this.record.ideasCount,
          'idea'
        )}`}
      >
        <FaIcon size={RECORD_FONT_SIZE} icon='lightbulb' style='solid' />
        <Text lineHeight={RECORD_LINE_HEIGHT} size={RECORD_FONT_SIZE}>
          {this.record.ideasCount}
        </Text>
      </FlexRow>
    );
  }

  get documentDate(): CanvaSXNode {
    if (this.record.typename !== 'Page' || !this.record.documentDate) {
      return null;
    }

    return (
      <FlexRow gap={2} verticalAlignment='center'>
        <FaIcon size={RECORD_FONT_SIZE} icon='calendar' />
        <Text lineHeight={RECORD_LINE_HEIGHT} size={RECORD_FONT_SIZE}>
          {moment(this.record.documentDateAsDate()).format('MMM D, YYYY')}
        </Text>
      </FlexRow>
    );
  }
}
